/**
 * Room statistics folded into jan…dec buckets, exactly as the Workspace grid
 * reads them.
 *
 * Lifted out of WorkspacePage so any tab that divides by rooms ($POR, $PAR,
 * ADR, RevPAR) uses the SAME denominators the grid shows. Two tabs computing
 * "occupied rooms" from the raw payload independently is how a GOP statement
 * and the grid it is derived from start disagreeing.
 *
 * Source precedence: the V3 room stats (which honour the version's
 * roomStatSource AND the closed-month actual roll-in useForecastData applies),
 * falling back to the flat rawRecords when V3 data is unavailable.
 */

import { useMemo } from 'react';
import type { ForecastPnLRecord, MonthlyRoomStats, RoomStatValues } from '../api';

const MONTH_NAMES = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

export interface ExpandedRoomStats {
  forecast: RoomStatValues;
  actual: RoomStatValues & {
    /** Recorded rooms revenue / ADR / RevPAR for closed-month roll-in */
    roomsRevenue?: number;
    adr?: number | null;
    revpar?: number | null;
  };
  budget: RoomStatValues;
  lyBudget?: RoomStatValues;
  projected?: {
    availableRooms: number;
    occupiedRooms: number;
    occupiedRoomsSource?: string;
    /** ADR/RevPAR fold in from the rooms revenue forecast */
    roomsRevenue?: number;
    adr?: number | null;
    revpar?: number | null;
  };
  overrides?: {
    availableRooms: number | null;
    occupiedRooms: number | null;
    hasAvailableOverride: boolean;
    hasOccupiedOverride: boolean;
  };
}

export interface RoomStatsByMonth {
  availableRooms: Record<string, number>;
  occupiedRooms: Record<string, number>;
  occupancyRate: Record<string, number>;
  expanded: Record<string, ExpandedRoomStats>;
}

export interface UseRoomStatsByMonthParams {
  /** useForecastData().roomStats — keyed 'YYYY-MM' */
  v3RoomStats?: Record<string, unknown> | null;
  rawRecords?: ForecastPnLRecord[];
}

export function buildRoomStatsByMonth({ v3RoomStats, rawRecords }: UseRoomStatsByMonthParams): RoomStatsByMonth {
  const emptyValues: RoomStatValues = { availableRooms: 0, occupiedRooms: 0, occupancyRate: 0 };
  const stats: RoomStatsByMonth = {
    availableRooms: {},
    occupiedRooms: {},
    occupancyRate: {},
    expanded: {},
  };

  // Initialize all months with 0
  for (const month of MONTH_NAMES) {
    stats.availableRooms[month] = 0;
    stats.occupiedRooms[month] = 0;
    stats.occupancyRate[month] = 0;
    stats.expanded[month] = {
      forecast: { ...emptyValues },
      actual: { ...emptyValues },
      budget: { ...emptyValues },
    };
  }

  // Use V3 room stats when available (these respect roomStatSource selection)
  if (v3RoomStats) {
    for (const [monthKey, rawMonthStats] of Object.entries(v3RoomStats)) {
      // Cast to full type with expanded data
      const monthStats = rawMonthStats as MonthlyRoomStats;
      // monthKey is like '2026-01', convert to 'jan'
      const monthNum = parseInt(monthKey.split('-')[1], 10);
      const monthName = MONTH_NAMES[monthNum - 1];
      if (monthName && monthStats) {
        // Top-level values (forecast based on selected source)
        stats.availableRooms[monthName] = monthStats.availableRooms || 0;
        stats.occupiedRooms[monthName] = monthStats.occupiedRooms || 0;
        stats.occupancyRate[monthName] = monthStats.occupancyRate || 0;

        // Expanded view data (actual, forecast, budget, projected, overrides)
        if (monthStats.forecast) {
          stats.expanded[monthName] = {
            lyBudget: monthStats.lyBudget,
            forecast: {
              availableRooms: monthStats.forecast.availableRooms || 0,
              occupiedRooms: monthStats.forecast.occupiedRooms || 0,
              occupancyRate: monthStats.forecast.occupancyRate || 0,
            },
            actual: {
              availableRooms: monthStats.actual?.availableRooms || 0,
              occupiedRooms: monthStats.actual?.occupiedRooms || 0,
              occupancyRate: monthStats.actual?.occupancyRate || 0,
              // Recorded rooms revenue / ADR / RevPAR — closed months show these
              roomsRevenue: monthStats.actual?.roomsRevenue || 0,
              adr: monthStats.actual?.adr ?? null,
              revpar: monthStats.actual?.revpar ?? null,
            },
            budget: {
              availableRooms: monthStats.budget?.availableRooms || 0,
              occupiedRooms: monthStats.budget?.occupiedRooms || 0,
              occupancyRate: monthStats.budget?.occupancyRate || 0,
            },
            // Pass through projected values (includes overrides applied to forecast)
            projected: monthStats.projected ? {
              availableRooms: monthStats.projected.availableRooms || 0,
              occupiedRooms: monthStats.projected.occupiedRooms || 0,
              occupiedRoomsSource: monthStats.projected.occupiedRoomsSource,
              roomsRevenue: monthStats.projected.roomsRevenue || 0,
              adr: monthStats.projected.adr ?? null,
              revpar: monthStats.projected.revpar ?? null,
            } : undefined,
            // Pass through override info for UI display (orange highlighting)
            overrides: monthStats.overrides ? {
              availableRooms: monthStats.overrides.availableRooms,
              occupiedRooms: monthStats.overrides.occupiedRooms,
              hasAvailableOverride: monthStats.overrides.hasAvailableOverride || false,
              hasOccupiedOverride: monthStats.overrides.hasOccupiedOverride || false,
            } : undefined,
          };
        }
      }
    }
    return stats;
  }

  // Fall back to rawRecords when V3 data isn't available
  if (!rawRecords || rawRecords.length === 0) {
    return stats;
  }

  // Room stats should be the same for all accounts in a given month
  // Just take the first record for each month
  const seenMonths = new Set<number>();
  for (const record of rawRecords) {
    if (seenMonths.has(record.monthNumber)) continue;
    seenMonths.add(record.monthNumber);

    const monthKey = MONTH_NAMES[record.monthNumber - 1];
    if (monthKey) {
      stats.availableRooms[monthKey] = record.roomsAvailable || 0;
      stats.occupiedRooms[monthKey] = record.roomsOccupied || 0;
      stats.occupancyRate[monthKey] = record.occupancyRate || 0;
      // For rawRecords fallback, use the same values for all expanded sources
      const pmsValues = {
        availableRooms: record.roomsAvailable || 0,
        occupiedRooms: record.roomsOccupied || 0,
        occupancyRate: record.occupancyRate || 0,
      };
      stats.expanded[monthKey] = {
        forecast: pmsValues,
        actual: { ...emptyValues },
        budget: { ...emptyValues },
      };
    }
  }

  return stats;
}

/**
 * Effective ARN per month matching what PnLGrid displays (including overrides).
 * An available-rooms override lives on the projected block, not the top level.
 */
export function effectiveAvailableRoomsFrom(roomStats: RoomStatsByMonth): Record<string, number> {
  const effective: Record<string, number> = {};
  for (const m of MONTH_NAMES) {
    const expanded = roomStats.expanded?.[m];
    const hasOverride = expanded?.overrides?.hasAvailableOverride || false;
    if (hasOverride) {
      effective[m] = expanded?.projected?.availableRooms ?? expanded?.forecast.availableRooms ?? roomStats.availableRooms[m] ?? 0;
    } else {
      effective[m] = roomStats.availableRooms[m] || 0;
    }
  }
  return effective;
}

/** Projected occupied rooms per month — mirrors the backend ZeroBase POR driver. */
export function effectiveOccupiedRoomsFrom(roomStats: RoomStatsByMonth): Record<string, number> {
  const effective: Record<string, number> = {};
  for (const m of MONTH_NAMES) {
    const expanded = roomStats.expanded?.[m];
    effective[m] = expanded?.projected?.occupiedRooms ?? expanded?.forecast?.occupiedRooms ?? roomStats.occupiedRooms[m] ?? 0;
  }
  return effective;
}

/** Memoized room-stat buckets for a tab that already has the forecast payload. */
export function useRoomStatsByMonth({ v3RoomStats, rawRecords }: UseRoomStatsByMonthParams): RoomStatsByMonth {
  return useMemo(() => buildRoomStatsByMonth({ v3RoomStats, rawRecords }), [v3RoomStats, rawRecords]);
}
